/**
 * Analytics — Performance view (REC-92 Phase 4).
 *
 * Wires up API latency, error rate, LLM cost, and admin activity sections.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { analyticsSearchSchema, buildDateRange } from '@/features/analytics/dateRange';
import { useAnalyticsPerformance } from '@/features/analytics/api';
import { useAdminActivity } from '@/features/analytics/performance/api';
import { LlmCostHeadline } from '@/features/analytics/performance/LlmCostHeadline';
import { LlmByPurposeBars } from '@/features/analytics/performance/LlmByPurposeBars';
import { LlmByModelBars } from '@/features/analytics/performance/LlmByModelBars';
import { RouteFilterSelect } from '@/features/analytics/performance/RouteFilterSelect';
import { LatencyChart } from '@/features/analytics/performance/LatencyChart';
import { ErrorAreaChart } from '@/features/analytics/performance/ErrorAreaChart';
import { TopErrorsTable } from '@/features/analytics/performance/TopErrorsTable';
import { AdminActivityTable } from '@/features/analytics/performance/AdminActivityTable';
import type {
  AnalyticsPerformanceRoute,
  AnalyticsLlmSummary,
} from '@honeycomb/shared/analytics/api';

export const Route = createFileRoute('/_authed/analytics/performance')({
  validateSearch: (raw: Record<string, unknown>) => {
    const parsed = analyticsSearchSchema.safeParse(raw);
    return parsed.success ? parsed.data : {};
  },
  component: PerformancePage,
});

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

/** Returns distinct route strings from the routes array. */
function distinctRoutes(routes: AnalyticsPerformanceRoute[]): string[] {
  return Array.from(new Set(routes.map((r) => r.route))).sort();
}

/**
 * Aggregate per-day latency rows.
 * When no filter: weighted average across all routes per day.
 * When filter set: return only that route's per-day rows.
 */
function aggregateLatencyByDate(
  routes: AnalyticsPerformanceRoute[],
  routeFilter: string | null,
): { date: string; p50: number; p95: number; p99: number }[] {
  const filtered = routeFilter
    ? routes.filter((r) => r.route === routeFilter)
    : routes;

  const byDate = new Map<
    string,
    { p50sum: number; p95sum: number; p99sum: number; totalReqs: number }
  >();

  for (const row of filtered) {
    const existing = byDate.get(row.date);
    if (existing) {
      existing.p50sum += row.latency_ms.p50 * row.request_count;
      existing.p95sum += row.latency_ms.p95 * row.request_count;
      existing.p99sum += row.latency_ms.p99 * row.request_count;
      existing.totalReqs += row.request_count;
    } else {
      byDate.set(row.date, {
        p50sum: row.latency_ms.p50 * row.request_count,
        p95sum: row.latency_ms.p95 * row.request_count,
        p99sum: row.latency_ms.p99 * row.request_count,
        totalReqs: row.request_count,
      });
    }
  }

  return Array.from(byDate.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, { p50sum, p95sum, p99sum, totalReqs }]) => ({
      date,
      p50: totalReqs > 0 ? p50sum / totalReqs : 0,
      p95: totalReqs > 0 ? p95sum / totalReqs : 0,
      p99: totalReqs > 0 ? p99sum / totalReqs : 0,
    }));
}

/**
 * Pivot routes to `{ date, [route]: error_count, ... }[]` for ErrorAreaChart.
 */
function buildErrorAreaRows(
  routes: AnalyticsPerformanceRoute[],
): Record<string, number | string>[] {
  const byDate = new Map<string, Record<string, number | string>>();

  for (const row of routes) {
    if (!byDate.has(row.date)) {
      byDate.set(row.date, { date: row.date });
    }
    const entry = byDate.get(row.date)!;
    const existing = typeof entry[row.route] === 'number' ? (entry[row.route] as number) : 0;
    entry[row.route] = existing + row.error_count;
  }

  return Array.from(byDate.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, entry]) => entry);
}

/**
 * Top N routes by total error count, with errorRate.
 */
function topRoutesByErrors(
  routes: AnalyticsPerformanceRoute[],
  n = 10,
): { route: string; errors: number; requests: number; errorRate: number }[] {
  const byRoute = new Map<string, { errors: number; requests: number }>();

  for (const row of routes) {
    const existing = byRoute.get(row.route);
    if (existing) {
      existing.errors += row.error_count;
      existing.requests += row.request_count;
    } else {
      byRoute.set(row.route, {
        errors: row.error_count,
        requests: row.request_count,
      });
    }
  }

  return Array.from(byRoute.entries())
    .map(([route, { errors, requests }]) => ({
      route,
      errors,
      requests,
      errorRate: requests > 0 ? errors / requests : 0,
    }))
    .sort((a, b) => b.errors - a.errors)
    .slice(0, n);
}

/** Sum all LLM rows into headline totals. */
function llmTotals(llm: AnalyticsLlmSummary[]): {
  cost_usd_cents: number;
  call_count: number;
  overall_success_rate: number;
} {
  let cost_usd_cents = 0;
  let call_count = 0;
  let success_count = 0;

  for (const row of llm) {
    cost_usd_cents += row.cost_usd_cents;
    call_count += row.call_count;
    success_count += row.success_count;
  }

  return {
    cost_usd_cents,
    call_count,
    overall_success_rate: call_count > 0 ? success_count / call_count : 0,
  };
}

type LlmGroupRow<K extends string> = Record<K, string> & {
  call_count: number;
  cost_usd_cents: number;
};

/**
 * Group LLM rows by `purpose` or `model`, summing cost + calls.
 */
function groupLlmBy<K extends 'purpose' | 'model'>(
  llm: AnalyticsLlmSummary[],
  key: K,
): LlmGroupRow<K>[] {
  const byKey = new Map<string, { call_count: number; cost_usd_cents: number }>();

  for (const row of llm) {
    const k = row[key];
    const existing = byKey.get(k);
    if (existing) {
      existing.call_count += row.call_count;
      existing.cost_usd_cents += row.cost_usd_cents;
    } else {
      byKey.set(k, { call_count: row.call_count, cost_usd_cents: row.cost_usd_cents });
    }
  }

  return Array.from(byKey.entries()).map(([k, totals]) => ({
    [key]: k,
    ...totals,
  })) as LlmGroupRow<K>[];
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

function PerformancePage() {
  const rawSearch = Route.useSearch();
  const range = buildDateRange({
    preset: rawSearch.preset,
    from: rawSearch.from,
    to: rawSearch.to,
  });

  const [routeFilter, setRouteFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const { data: perf, isLoading, isError } = useAnalyticsPerformance(range);
  const { data: activity } = useAdminActivity(range, { page, perPage: 50 });

  if (isLoading) {
    return <div className="text-ink-soft text-sm py-8">Loading performance…</div>;
  }

  if (isError || !perf) {
    return (
      <div
        className="rounded-xl p-4 text-sm"
        style={{
          background: 'var(--color-error-bg)',
          border: '1px solid var(--color-error-border)',
          color: 'var(--color-error-text)',
        }}
      >
        Failed to load performance analytics. The rollup data may not be available yet —
        try refreshing or triggering a manual rollup.
      </div>
    );
  }

  const totals = llmTotals(perf.llm);
  const filteredRoutes = routeFilter
    ? perf.routes.filter((r) => r.route === routeFilter)
    : perf.routes;

  return (
    <div className="flex flex-col gap-8" data-testid="analytics-performance-page">
      {/* 1. LLM health */}
      <section className="flex flex-col gap-4" data-testid="analytics-perf-llm-table">
        <h2 className="font-serif text-lg">LLM health</h2>
        <LlmCostHeadline
          total_cost_cents={totals.cost_usd_cents}
          total_calls={totals.call_count}
          overall_success_rate={totals.overall_success_rate}
        />
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div>
            <h3 className="text-sm font-medium text-ink-soft mb-2">By purpose</h3>
            <LlmByPurposeBars data={groupLlmBy(perf.llm, 'purpose')} />
          </div>
          <div>
            <h3 className="text-sm font-medium text-ink-soft mb-2">By model</h3>
            <LlmByModelBars data={groupLlmBy(perf.llm, 'model')} />
          </div>
        </div>
      </section>

      {/* 2. API latency */}
      <section className="flex flex-col gap-4" data-testid="analytics-perf-routes-table">
        <div className="flex items-center justify-between gap-4">
          <h2 className="font-serif text-lg">API latency</h2>
          <RouteFilterSelect
            routes={distinctRoutes(perf.routes)}
            value={routeFilter}
            onChange={setRouteFilter}
          />
        </div>
        <LatencyChart rows={aggregateLatencyByDate(filteredRoutes, routeFilter)} />
      </section>

      {/* 3. Error rate */}
      <section className="flex flex-col gap-4">
        <h2 className="font-serif text-lg">Error rate</h2>
        <ErrorAreaChart
          rows={buildErrorAreaRows(perf.routes)}
          caption="Breakdown by error_code pending REC-87 follow-up."
        />
        <TopErrorsTable rows={topRoutesByErrors(perf.routes)} />
      </section>

      {/* 4. Image generation — placeholder */}
      <section className="flex flex-col gap-4">
        <h2 className="font-serif text-lg">Image generation</h2>
        <div className="rounded-lg border border-hairline p-4 text-ink-soft text-sm">
          Image generation metrics pending REC-87 follow-up — depends on REC-83
          image-gen events landing in the rollup.
        </div>
      </section>

      {/* 5. Admin activity */}
      <section className="flex flex-col gap-4">
        <h2 className="font-serif text-lg">Admin activity</h2>
        <AdminActivityTable
          events={activity?.events ?? []}
          page={page}
          perPage={50}
          total={activity?.total ?? 0}
          onPageChange={setPage}
        />
      </section>
    </div>
  );
}
